import { API_ROUTES, type ApiSnapshot, type ProtocolEvent } from '@quintessence/core';
import type { DataSource, SourceMessage } from './types';

const POLL_MS = 4000;

/** Reads the agent runtime's HTTP API: polls the full view, streams events over SSE. */
export class LiveSource implements DataSource {
  readonly kind = 'live' as const;
  private timer: ReturnType<typeof setInterval> | null = null;
  private stream: EventSource | null = null;
  private stopped = false;

  constructor(private readonly baseUrl: string) {}

  static async reachable(baseUrl: string, timeoutMs = 3500): Promise<boolean> {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    try {
      const res = await fetch(`${baseUrl}${API_ROUTES.health}`, { signal: ctrl.signal });
      // A static host or dev server may answer any path with index.html; only a JSON health check counts.
      return res.ok && (res.headers.get('content-type') ?? '').includes('application/json');
    } catch {
      return false;
    } finally {
      clearTimeout(timer);
    }
  }

  start(emit: (msg: SourceMessage) => void): void {
    this.stopped = false;
    emit({ type: 'status', status: 'connecting' });
    const poll = async () => {
      try {
        const res = await fetch(`${this.baseUrl}${API_ROUTES.view}?timeline=900&feed=150`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const snapshot = (await res.json()) as ApiSnapshot;
        if (!this.stopped) {
          emit({ type: 'snapshot', snapshot });
          emit({ type: 'status', status: 'running' });
        }
      } catch {
        if (!this.stopped) emit({ type: 'status', status: 'error' });
      }
    };
    void poll();
    this.timer = setInterval(poll, POLL_MS);

    if (typeof EventSource !== 'undefined') {
      this.stream = new EventSource(`${this.baseUrl}${API_ROUTES.events}`);
      this.stream.addEventListener('protocol', (msg) => {
        try {
          const event = JSON.parse((msg as MessageEvent<string>).data) as ProtocolEvent;
          emit({ type: 'events', events: [event] });
        } catch {
          // Ignore malformed frames; the next poll carries the authoritative feed.
        }
      });
    }
  }

  stop(): void {
    this.stopped = true;
    if (this.timer) clearInterval(this.timer);
    this.stream?.close();
    this.stream = null;
  }
}
